/**
 * Hook for resolving the current user's tier from their RBAC permissions.
 *
 * Resolves the tier **org-wide**: it gathers the permissions from every role the
 * user holds in the selected organization (org-level and any program-level role
 * within it) plus global roles, then runs them through the pure
 * {@link resolveUserTier}. This is deliberately broader than the per-program
 * `useCurrentUserContextRoles` (which is still used for per-action gating) so the
 * org-wide Tier-2 experience is consistent regardless of which program is active.
 */

import { useStore } from '@store/index';
import { useMemo } from 'react';

import {
  collectOrgPermissions,
  resolveEffectiveTier,
  resolveUserTier,
  type UserTier,
} from '@/utils/userTier';

import { useOrgPqcWorkflows } from './useOrgPqcWorkflows';
import { useCurrentUserRoles } from './useUsers';

interface UseUserTierResult {
  /**
   * The effective tier the app should render — the permission-derived tier folded
   * with the user's Org-wide Cases opt-in. Consumed by every tier gating
   * site (CasesRouter, the landing redirects, the sidebar nav link).
   */
  tier: UserTier;
  /**
   * The raw, permission-derived tier, independent of the opt-in preference. Use
   * this to decide whether the opt-in toggle is meaningful for the user.
   */
  permissionTier: UserTier;
  /**
   * Whether the user is eligible for Org-wide Cases: their permissions resolve
   * to tier2 AND the selected org exposes PQC workflows to them. PQC-scoped so
   * the feature never surfaces (no toggle, no routing) for non-PQC orgs/flows.
   */
  isEligible: boolean;
  isLoading: boolean;
}

export function useUserTier(): UseUserTierResult {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const orgWideCasesEnabled = useStore(state => state.orgWideCasesEnabled);

  // Same query useCurrentUserContextRoles runs (shared cache key → no extra fetch);
  // returns the user's unfiltered roles. Org-wide filtering happens in collectOrgPermissions.
  const { data, isLoading: rolesLoading } = useCurrentUserRoles(
    selectedOrganizationId ?? undefined,
    selectedProgramId ?? undefined,
  );

  const permissionTier = useMemo(
    () => resolveUserTier(collectOrgPermissions(data?.user_roles || [], selectedOrganizationId)),
    [data?.user_roles, selectedOrganizationId],
  );

  // PQC-scope the feature: it's only available when the org actually exposes PQC
  // workflows to the user. Gated on permission-eligibility so non-eligible users
  // never hit the org-wide workflows endpoint.
  const { pqcWorkflows, isLoading: pqcLoading } = useOrgPqcWorkflows({
    enabled: permissionTier === 'tier2',
  });
  const isEligible = permissionTier === 'tier2' && pqcWorkflows.length > 0;

  // The opt-in preference decides whether an eligible user actually gets the
  // org-wide experience; everyone else stays 'standard'.
  const tier = resolveEffectiveTier(isEligible, orgWideCasesEnabled);

  return { tier, permissionTier, isEligible, isLoading: rolesLoading || pqcLoading };
}
